import React, { useState, useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { UploadIcon, XIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

export interface UploadedFile {
  url: string
  file_name: string
  file_type: string
  rfq_id: string | null
}

interface FileUploaderProps {
  onFilesUploaded: (files: UploadedFile[]) => void
  existingFiles?: UploadedFile[]
  maxSize?: number // in bytes
  maxFiles?: number
  acceptedFileTypes?: Record<string, string[]>
  className?: string
}

export function FileUploader({
  onFilesUploaded,
  existingFiles = [],
  maxSize = 5242880, // 5MB default
  maxFiles = 5,
  acceptedFileTypes = {
    'image/*': ['.jpeg', '.jpg', '.png'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
  },
  className
}: FileUploaderProps) {
  const [files, setFiles] = useState<UploadedFile[]>(existingFiles)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState("")

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (files.length + acceptedFiles.length > maxFiles) {
      setError(`You can only upload a maximum of ${maxFiles} files.`)
      return
    }

    setUploading(true)
    setError("")

    try {
      const uploadedFiles: UploadedFile[] = []
      
      // Simulate file upload with progress
      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i]
        
        // For demo purposes, we're creating URLs with object URLs
        // In a real app, you would upload to your server/storage here
        const progress = ((i + 1) / acceptedFiles.length) * 100
        setUploadProgress(progress)
        
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 500))
        
        const fileUrl = URL.createObjectURL(file)
        
        uploadedFiles.push({
          url: fileUrl,
          file_name: file.name,
          file_type: file.type,
          rfq_id: null
        })
      }
      
      const newFiles = [...files, ...uploadedFiles]
      setFiles(newFiles)
      onFilesUploaded(newFiles)
    } catch (error) {
      console.error("Error uploading files:", error)
      setError("An error occurred while uploading files. Please try again.")
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }, [files, maxFiles, onFilesUploaded])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize,
    maxFiles: maxFiles - files.length,
    accept: acceptedFileTypes
  })
  
  const removeFile = (index: number) => {
    const newFiles = [...files]
    newFiles.splice(index, 1)
    setFiles(newFiles)
    onFilesUploaded(newFiles)
  }
  
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes'
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    else return (bytes / 1048576).toFixed(1) + ' MB'
  }
  
  const acceptedFileTypesText = Object.values(acceptedFileTypes)
    .flat()
    .join(', ')
    .replace(/\./g, '')
    .toUpperCase()

  return (
    <div className={cn("space-y-4", className)}>
      <div 
        {...getRootProps()} 
        className={cn(
          "border-2 border-dashed rounded-lg p-6 cursor-pointer flex flex-col items-center justify-center text-center",
          isDragActive ? "border-primary bg-primary/5" : "border-border",
          uploading && "pointer-events-none opacity-60"
        )}
      >
        <input {...getInputProps()} />
        <UploadIcon className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-sm font-medium mb-1">
          {isDragActive 
            ? "Drop the files here..." 
            : "Drag & drop files here, or click to select files"
          }
        </p>
        <p className="text-xs text-muted-foreground mb-2">
          {`Upload up to ${maxFiles} files (${acceptedFileTypesText}, max ${formatFileSize(maxSize)} each)`}
        </p>
        <Button variant="secondary" size="sm" disabled={uploading}>
          Select Files
        </Button>
      </div>
      
      {error && (
        <div className="text-destructive text-sm">{error}</div>
      )}
      
      {uploading && (
        <div className="space-y-2">
          <Progress value={uploadProgress} />
          <p className="text-xs text-center text-muted-foreground">
            Uploading... {Math.round(uploadProgress)}%
          </p>
        </div>
      )}
      
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">
            {files.length} {files.length === 1 ? "file" : "files"} attached
          </p>
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li key={index} className="flex items-center justify-between bg-muted/40 rounded-md p-2">
                <div className="flex items-center truncate">
                  <div className="w-8 h-8 bg-background border rounded-md flex items-center justify-center mr-2">
                    <span className="text-xs font-medium">
                      {file.file_type.includes('image') ? 'IMG' : 
                       file.file_type.includes('pdf') ? 'PDF' :
                       file.file_type.includes('word') ? 'DOC' :
                       file.file_type.includes('sheet') ? 'XLS' : 'FILE'}
                    </span>
                  </div>
                  <span className="text-sm truncate">{file.file_name}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6" 
                  onClick={() => removeFile(index)}
                >
                  <XIcon className="h-4 w-4" />
                  <span className="sr-only">Remove file</span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
